import { Trophy, Clock, GraduationCap, Goal, X } from "lucide-react"
import { Checkbox } from "@/components/ui/checkbox"
import { Button } from "@/components/ui/button"
import { useNavigate, useParams } from "react-router-dom"
import { motion } from "framer-motion"
import useGetWeeklyObjectivesApproval from "@/modules/planner/queries/use-get-weekly-objectives-approval"
import { formatDuration } from "@/shared/utils/date-fn"
import { intervalToDuration } from "date-fns"
import { useMemo } from "react"
import { ObjectiveCard } from "@/modules/planner/components/comunications/objective-card"

export default function WeeklyObjetiveAchieved() {
  const navigate = useNavigate()
  const { id } = useParams()
  const { data, isPending, isError } = useGetWeeklyObjectivesApproval(id as string)

  const totalTime = useMemo(() => {
    if (!data) return
    const duration = intervalToDuration({ start: 0, end: data?.totalTime * 10000 })

    const formattedDuration = formatDuration(duration).min({
      hours: true,
      minutes: true,
    })

    return formattedDuration
  }, [data])

  function handleContinue() {
    if (data?.skippedActivities) {
      navigate(`/comunications/${id}/saved-activities`, { replace: true })
    } else {
      navigate("/planner", { replace: true })
    }
  }

  if (isError) {
    return (
      <section className="z-5 absolute left-0 top-0 flex size-full flex-col items-center justify-center gap-4 bg-white px-2 py-2 lg:gap-6 lg:p-10">
        <h1 className="text-center text-3xl font-bold text-primary">
          Error al obtener el objetivo semanal
        </h1>
        <p className="text-center font-semibold text-muted-foreground">
          Por favor, intenta nuevamente más tarde.
        </p>
        <Button onClick={() => navigate("/my-situation", { replace: true })}>
          Volver al inicio
        </Button>
      </section>
    )
  }

  return (
    <section className="z-5 absolute left-0 top-0 flex size-full flex-col items-center justify-center gap-4 bg-white px-2 py-2 lg:gap-6 lg:p-10">
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-6 top-6"
        onClick={() => navigate("/planner", { replace: true })}
      >
        <X className="!size-7 text-muted-foreground" />
      </Button>

      {/* Trophy icon with gradient background */}
      <div className="relative mb-4 flex items-center justify-center">
        <motion.div
          className="absolute size-28 rounded-full bg-gradient-to-b from-primary-50 to-primary-100"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
        />
        <motion.div
          className="relative flex size-24 items-center justify-center rounded-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.4 }}
        >
          <Trophy className="size-12 text-primary" strokeWidth={1.5} />
        </motion.div>
      </div>

      {/* Title and subtitle */}
      <motion.h1
        className="text-center text-3xl font-bold text-primary"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.7, delay: 0.4 }}
      >
        ¡Objetivo semanal cumplido!
      </motion.h1>
      <motion.p
        className="text-center font-semibold text-muted-foreground"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 1.2 }}
      >
        Al completar esta actividad, cumpliste el objetivo semanal y la rompiste. ¡Seguí así, crack!
        🚀
      </motion.p>

      {/* Objective card */}
      <motion.div
        className="mb-6 w-fit rounded-lg bg-primary-50 px-4 py-3"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.2 }}
      >
        <div className="flex items-center gap-2">
          <Checkbox checked className="size-4 bg-primary-500 shadow-md" />
          <span className="font-medium text-secondary-foreground">
            <strong className="mr-1 text-primary-500">Objetivo:</strong>
            <span>{data?.name}</span>
          </span>
        </div>
      </motion.div>

      {/* Stats cards */}
      <motion.div
        className="grid w-full max-w-4xl grid-cols-1 gap-4 md:grid-cols-3"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.2 }}
      >
        {/* Time dedicated */}
        <ObjectiveCard
          isPending={isPending}
          title="Tiempo dedicado"
          Icon={Clock}
          value={totalTime ?? ""}
        />

        {/* Points obtained */}
        <ObjectiveCard
          isPending={isPending}
          title="Puntaje obtenido"
          Icon={GraduationCap}
          value={`${data?.score} Pts`}
        />

        {/* Weekly objectives */}
        <ObjectiveCard
          isPending={isPending}
          title="Objetivos semanales"
          Icon={Goal}
          value={`${data?.approvedWeeklyObjectives} de ${data?.totalWeeklyObjectives} hechos`}
        />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.2 }}
      >
        <Button className="mt-12 w-fit bg-primary-500 font-semibold" onClick={handleContinue}>
          Continuar
        </Button>
      </motion.div>
    </section>
  )
}
